<template>
    <PublicHeader />
    <AlertsComponent :errors="generalErrors" />
    <section class="container">
      <div class="login flex-column">
            <h2 class="sm-fs-xl mb-20">Register for an Account </h2>
            <!-- Role Selection -->
            <div class="container mt-35 sm-mt-0 sm-p-0 logo-header">
                <div class="row">
                    <div class="col-md-8 sm-pdl-0 sm-pdr-0 order-2 order-md-1">
                        <h4 class="text-start">Choose an entity from below to create an account.</h4>
                        <div class="d-flex gap-20 mt-30">
                            <div class="role-selection mt-10 mb-20">
                                <label class="pdr-15" for="participant">
                                    <input type="radio" id="participant"  name="UserType" v-model="selectedUserType" :value="1" />
                                    Participant
                                </label>
                                <label class="pdr-15" for="local">
                                    <input type="radio" id="local"  name="UserType" v-model="selectedUserType" :value="5" />
                                    Locals
                                </label>
                                <label class="pdr-15" for="employer">
                                    <input type="radio" id="employer"  name="UserType"  v-model="selectedUserType" value="6" />
                                    Employers
                                </label>
                                <label class="pdr-15" for="payroll_company">
                                    <input type="radio" id="payroll_company"  name="UserType" v-model="selectedUserType" value="7" />
                                    Payroll Company<br/>
                                    <small class="sm-fs-xxs">(Paying Agent)</small>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div class="mb-50">
                <div class="container flex-md-row sm-pdl-0 sm-pdr-0">
                    <div class="row">
                        <div class="col-md-12 mb-20">
                            <LocalOptions v-if="selectedUserType != 1" :selectedUserType="selectedUserType" :registerType="registerType" @change-type="handleTypeChange" />
                        </div>
                    </div>
                    <div class="sm-mt-0 d-flex flex-row gap-30">
                        <form class="w-100">
                            <ParticipantFields v-if="selectedUserType === 1" :securityQuestions="securityQuestions" :errors="errors"
                                :loadingSubmit="loadingSubmit" @register="submitForm" @reset-errors="resetErrrors">
                            </ParticipantFields>
                            <LocalFields v-else-if="selectedUserType == 5 || selectedUserType == 3"
                                :securityQuestions="securityQuestions" :states="states" @register="submitForm" @reset-errors="resetErrrors"
                                :loadingSubmit="loadingSubmit" :localRequest="localRequest" :authRequest="authRequest" :errors="errors"
                                :registerType="registerType" :success="success" />
                            <EmployerFields v-else-if="selectedUserType == 6 || selectedUserType == 7"
                                :securityQuestions="securityQuestions" :employerRequest="employerRequest" @reset-errors="resetErrrors"
                                :employerType="employerType" :states="states" :loadingSubmit="loadingSubmit"
                                @register="submitForm" :errors="errors" :success="success"/>
                            <!-- <span class="body-text forgot-credentials-text sm-fs-sm d-flex gap-10 flex-column flex-md-row sm-mt-20 align-items-md-start align-items-center">
                                <span>
                                    Already have an Account?
                                    <router-link :to="{ name: 'login' }"> Log in</router-link>
                                </span>
                            </span> -->
                        </form>
                        <div class="w-100 hide-mobile">
                            <img style="max-width: 100%; max-height: 90vh;" class="rounded img-fluid" src="@/assets/images/registration.png" alt="2FA Image" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </section>
    <PublicFooter />
</template>

<script lang="ts">
import { Ref, onMounted, ref, watch } from 'vue';
import { ILocalAuth, SelectOption } from '@/interfaces/interfaces';
import { PendingLocal, PendingEmployer } from '@/interfaces/admin';
import { useAuthStore } from '@/stores/auth';
import { useRoute, useRouter } from 'vue-router';
import { useOptionsFormatter } from "@utils/optionFormatter";
import ParticipantFields from '@/components/register/ParticipantFields.vue';
import EmployerFields from '@/components/register/employer/EmployerFields.vue';
import LocalOptions from '@/components/register/local/LocalOptions.vue';
import LocalFields from '@/components/register/local/LocalFields.vue';
import PublicHeader from '@components/PublicHeader.vue';
import PublicFooter from '@components/PublicFooter.vue';
import axios from 'axios';
import { useModalStore } from '@/stores/modal';

export default {
    components: {
        ParticipantFields,
        EmployerFields,
        LocalFields,
        LocalOptions,
        PublicHeader,
        PublicFooter
    },
    setup() {
        const selectedUserType: Ref<string | number> = ref(1);
        const errors: Ref<string[]> = ref([]);
        const generalErrors: Ref<string[]> = ref([]);
        const securityQuestions: Ref<SelectOption[]> = ref([]);
        const localRequest: Ref<PendingLocal | null> = ref(null);
        const authRequest: Ref<ILocalAuth | null> = ref(null);
        const states: Ref<SelectOption[]> = ref([]);
        const employerRequest: Ref<PendingEmployer | null> = ref(null);
        const registerType: Ref<string> = ref('auth');
        const employerType: Ref<string> = ref('auth');
        const success = ref(null);
        const authStore = useAuthStore();
        const router = useRouter();
        const hash = useRoute().query.Hash;
        const loadingSubmit = ref(false);
        const localRole = ref('local');

        watch(selectedUserType, () => errors.value = []);
        watch(registerType, () => errors.value = []);
        watch(employerType, () => errors.value = []);

        const handleTypeChange = (payload: string, type: string) => {
            if(type === 'local') {
                registerType.value = payload;
            } else {
                employerType.value = payload;
            }
        }

        const resetErrrors = () => {
            errors.value = [];
        }

        // Fetch Data
        const fetchData = async () => {
            await axios.get('api/register', { params: { Hash: hash, RequestID: useRoute()?.query?.RequestID ?? null } })
                .then(response => {
                    securityQuestions.value = useOptionsFormatter(response.data.SecurityQuestions);
                    localRequest.value = response.data.UILocalRequest;
                    authRequest.value = response.data.AuthRequest;
                    registerType.value = response.data.RegisterType;
                    employerType.value = response.data.RegisterType;
                    employerRequest.value = response.data.UIEmployerRequest;
                    states.value = useOptionsFormatter(response.data.States);
                    selectedUserType.value = response.data.UserType == 3 ? 5 : response.data.UserType;
                })
        };

        // Function to submit the form
        const submitForm = async (user: any) => {
            loadingSubmit.value = true;
            let userType = selectedUserType.value;

            if(userType == 5) {
                userType = registerType.value == 'rep_register' ? 3 : 5;
            }

            try {
                const formData = {
                    UserType: userType,
                    EmployerType: employerType.value ?? null,
                    LocalType: registerType.value ?? null,
                    AuthRequest: authRequest.value?.UILocalRequestID ?? null,
                    User: user
                };

                const response = await axios.post('api/register', { ...formData });
                success.value = response.data.success;
                const route = response.data?.route;
                const role = response.data?.role;
                errors.value = [];
                user.value = null;

                if(success.value && !route) {
                    useModalStore().openModal('success-modal', null, 'modal-success', null, {
                        title: registerType.value === 'auth' ? 'Auth Request' : 'Register Request',
                        content: {
                            type: "text",
                            value: response.data.success,
                            icon: 'iatse-icon-user-verified'
                        },
                    })
                }

                if (route) {
                    const credentials = { email: user.Email, password: user.Password, role: role };

                    setTimeout(async () => {
                        await authStore.authenticateUser(credentials);
                        // await authStore.loginUser();

                    }, 500);
                }
            } catch (error: any) {
                if (error.response.data.error) {
                    errors.value = error.response.data.error;
                    generalErrors.value = error.response.data.error?.General ? Object.values(
                        error.response.data.error?.General
                    ).flat() as string[] : [];
                }
            }

            loadingSubmit.value = false;

            setTimeout(() => {
                success.value = null;
                generalErrors.value = [];
            }, 5000);
        };

        onMounted(async () => {
            authStore.clearUserStore();
            authStore.logoutWithoutPush();
            await fetchData()
        });

        return {
            errors,
            generalErrors,
            success,
            selectedUserType,
            securityQuestions,
            registerType,
            localRequest,
            authRequest,
            states,
            localRole,
            employerType,
            employerRequest,
            loadingSubmit,
            submitForm,
            resetErrrors,
            handleTypeChange
        };
    },
};
</script>